import { menus } from '@/constants/menus';
import { useFeatureFlags } from '@/features/feature-flag/feature-flag.hooks';
import { isRouteWithin } from '@/lib/route-match';
import type { IMenu } from '@/types/menu';
import { useCallback, useEffect, useState } from 'react';
import { useLoaderData, useLocation } from 'react-router';

const useMenu = () => {
  const location = useLocation();
  const data = useLoaderData();
  const permissions = data?.permissions;
  const { isEnabled: isFeatureEnabled, isLoading: isFlagsLoading } =
    useFeatureFlags();

  // const [headerTitle, setHeaderTitle] = useState<string | undefined>();
  const [currentRoute, setCurrentRoute] = useState<IMenu | undefined>();
  const [currentParent, setCurrentParent] = useState<IMenu | undefined>();
  const [accordion, setAccordion] = useState<string | undefined>();
  const [currentChildMenu, setCurrentChildMenu] = useState<
    string | undefined
  >();
  const [collapseSidebar, setCollapseSidebar] = useState<boolean>(true);

  const findActiveMenu = useCallback(
    (
      items: IMenu[],
      path: string,
      parent?: IMenu
    ): { matched?: IMenu; parent?: IMenu } => {
      for (const item of items) {
        if (item.children) {
          const found = findActiveMenu(item.children, path, item);
          if (found.matched) return found;
          // A parent is only a container for its children. Matching its own
          // route as well would claim every unlisted page under that prefix —
          // and hand it the parent's permission key instead of the page's.
          continue;
        }

        if (item.route && isRouteWithin(path, item.route)) {
          return { matched: item, parent };
        }
      }
      return {};
    },
    []
  );

  const isMenuActive = (menu: IMenu) => {
    if (menu.children) {
      return !!menu.children.find(child => child.label === currentChildMenu);
    }
    return isRouteWithin(location.pathname, menu.route ?? '');
  };

  const canViewMenu = useCallback(
    (key?: string) => key && permissions?.[key]?.canView === true,
    [permissions]
  );

  const getVisibleMenusInSection = (sectionMenus: IMenu[]) => {
    return sectionMenus.filter(menu => {
      if (!isAvailable(menu)) return false;
      if (menu.key) {
        return canViewMenu(menu.key);
      }
      if (menu.children) {
        return menu.children.some(
          child => child.key && canViewMenu(child.key) && isAvailable(child)
        );
      }
      return true;
    });
  };

  useEffect(() => {
    const { parent, matched } = findActiveMenu(menus, location.pathname);

    // setHeaderTitle(matched?.label);
    setCurrentRoute(matched);
    setCurrentParent(parent);

    if (parent) {
      setAccordion(parent.label);
      setCurrentChildMenu(matched?.label);
    } else {
      setAccordion(matched?.label);
      setCurrentChildMenu(undefined);
    }
  }, [location.pathname, findActiveMenu]);

  /**
   * The children of a tabbed menu that this role can open, in menu order —
   * the tab strip's tabs, and the first one is where the sidebar link goes.
   */
  /** Open to this company: its `feature` release flag is on. */
  const isAvailable = useCallback(
    (menu: Pick<IMenu, 'feature'>) => isFeatureEnabled(menu.feature),
    [isFeatureEnabled]
  );

  const getVisibleTabs = useCallback(
    (menu?: IMenu) =>
      menu?.tabs
        ? (menu.children ?? []).filter(
            child => canViewMenu(child.key) && isAvailable(child)
          )
        : [],
    [canViewMenu, isAvailable]
  );

  /**
   * The page carries its section's own grouped side nav (`SectionNav`, e.g.
   * Settings -> Company): two navigation columns compete for the width.
   */
  const sectionTabs = getVisibleTabs(currentParent);
  const hasSectionNav =
    sectionTabs.some(tab => tab.group) &&
    sectionTabs.some(tab => tab.route === location.pathname);

  return {
    currentRoute,
    /** The menu the current page sits under, when it is a child entry. */
    currentParent,
    setCurrentRoute,
    accordion,
    setAccordion,
    currentChildMenu,
    collapseSidebar,
    setCollapseSidebar,
    isMenuActive,
    getVisibleMenusInSection,
    canViewMenu,
    isAvailable,
    getVisibleTabs,
    hasSectionNav,
    /**
     * The tenant's flags are not in yet (first load, or a tenant switch), so
     * which entries exist is unknown: draw placeholders, not a guessed menu.
     */
    isMenuLoading: isFlagsLoading,
  };
};

export default useMenu;
